// 守狸 Web 完整端同步引擎（P2）。
//
// 数据模型是「单 VDK 整包加密」——server 只存不透明 blob + 整数版本，OCC 用 baseVersion
// 乐观锁（不等当前版本即 409 version_conflict）。没有 per-item DEK 粒度（推迟 Phase C 团队共享）。
// 故并发冲突在**已解密的 payload 对象层**做实体级三路合并，而非密文层：
//   编辑 → reseal 整包 → upload(baseVersion)；若 409 → 拉远端解出 P_remote → 三路合并 → 重试。
//
// 三路合并粒度 = payload 里的实体集合（accounts/secrets/categories/projects 按 id）：
//   - 新增（仅一侧有、base 无）：并入
//   - 删除（base 有、一侧删、另一侧未改）：删除胜出且幂等
//   - 删除 vs 编辑（一侧删、另一侧改）：**编辑胜出**（保守，不丢用户改动）
//   - 同 id 双改：按 updated_at last-write-wins per 实体 + 标记冲突（非阻塞提示）
// 服务端权威 / 设备本地字段：cloud 取 remote，audit 取并集去重，recent/ignored 取本地并集。

import { packVault, resealPayload, bytesToBase64 } from './crypto'
import * as api from './api'

export interface Entity {
  id: string
  updated_at?: string
  [k: string]: unknown
}

export interface VaultPayload {
  accounts?: Entity[]
  secrets?: Entity[]
  categories?: Entity[]
  projects?: Entity[]
  risks?: unknown[]
  ignored_risk_keys?: string[]
  recent?: string[]
  audit?: Entity[]
  cloud?: unknown
  [k: string]: unknown
}

/** 一条冲突记录：同一实体在本地与远端都改过，已自动取较新版本，供 UI 非阻塞提示。 */
export interface MergeConflict {
  collection: string
  id: string
  chosen: 'local' | 'remote'
}

export interface MergeResult {
  merged: VaultPayload
  conflicts: MergeConflict[]
}

const ENTITY_COLLECTIONS = ['accounts', 'secrets', 'categories', 'projects'] as const

function byId(list: Entity[] | undefined): Map<string, Entity> {
  const m = new Map<string, Entity>()
  for (const e of list ?? []) m.set(e.id, e)
  return m
}

/** 稳定序列化做「是否改过」判定（key 排序，避免字段顺序误判）。 */
function stable(e: Entity | undefined): string {
  if (!e) return ''
  const keys = Object.keys(e).sort()
  return JSON.stringify(e, keys)
}

/** updated_at 较新者（ISO 字符串字典序即时间序；缺失视为最旧）。 */
function newer(a: Entity, b: Entity): Entity {
  return (a.updated_at ?? '') >= (b.updated_at ?? '') ? a : b
}

function mergeCollection(
  collection: string,
  base: Entity[] | undefined,
  local: Entity[] | undefined,
  remote: Entity[] | undefined,
  conflicts: MergeConflict[],
): Entity[] {
  const B = byId(base)
  const L = byId(local)
  const R = byId(remote)
  const ids = new Set<string>([...L.keys(), ...R.keys(), ...B.keys()])
  const out: Entity[] = []

  for (const id of ids) {
    const b = B.get(id)
    const l = L.get(id)
    const r = R.get(id)
    const inB = !!b
    const inL = !!l
    const inR = !!r

    if (inL && inR) {
      // 双方都在：看各自相对 base 是否改过。
      const lChanged = stable(l) !== stable(b)
      const rChanged = stable(r) !== stable(b)
      if (lChanged && rChanged && stable(l) !== stable(r)) {
        const win = newer(l!, r!)
        out.push(win)
        conflicts.push({ collection, id, chosen: win === l ? 'local' : 'remote' })
      } else if (lChanged) {
        out.push(l!)
      } else {
        out.push(r!) // 本地未改（或两边同改成一样）→ 取远端
      }
    } else if (inL && !inR) {
      // 远端没有：要么远端删了，要么本地新增。
      if (!inB) {
        out.push(l!) // 本地新增
      } else if (stable(l) !== stable(b)) {
        out.push(l!) // 删除 vs 本地编辑 → 编辑胜出（保守，不丢改动）
        conflicts.push({ collection, id, chosen: 'local' })
      }
      // 否则：本地未改 + 远端已删 → 接受删除（不 push）
    } else if (!inL && inR) {
      // 本地没有：对称——本地删了，或远端新增。
      if (!inB) {
        out.push(r!) // 远端新增
      } else if (stable(r) !== stable(b)) {
        out.push(r!) // 本地删 vs 远端编辑 → 编辑胜出
        conflicts.push({ collection, id, chosen: 'remote' })
      }
      // 否则：远端未改 + 本地已删 → 接受删除
    }
    // 两边都无 → 双删，幂等丢弃
  }
  return out
}

/** 按 id 取并集去重（audit：append-only，两侧都保留）。 */
function unionById(local: Entity[] | undefined, remote: Entity[] | undefined): Entity[] {
  const m = new Map<string, Entity>()
  for (const e of remote ?? []) m.set(e.id, e)
  for (const e of local ?? []) m.set(e.id, e)
  return [...m.values()]
}

function unionStr(a: string[] | undefined, b: string[] | undefined): string[] {
  return [...new Set([...(a ?? []), ...(b ?? [])])]
}

/**
 * 三路合并：base = 拉取时快照，local = 本地编辑后，remote = 服务端最新（409 后重拉解出）。
 * 返回 merged + 冲突清单（非阻塞，供 UI 提示）。
 */
export function mergePayload(
  base: VaultPayload,
  local: VaultPayload,
  remote: VaultPayload,
): MergeResult {
  const conflicts: MergeConflict[] = []
  const merged: VaultPayload = { ...local }

  for (const c of ENTITY_COLLECTIONS) {
    merged[c] = mergeCollection(c, base[c], local[c], remote[c], conflicts)
  }

  // 服务端权威：订阅态 / 应急配额 / S_server 状态等以远端为准。
  merged.cloud = remote.cloud ?? local.cloud
  // 审计 append-only：两侧并集去重。
  merged.audit = unionById(local.audit, remote.audit)
  // 设备本地 UI 态：最近访问取本地；忽略的风险键取并集（用户在任一端忽略都尊重）。
  merged.recent = local.recent ?? []
  merged.ignored_risk_keys = unionStr(local.ignored_risk_keys, remote.ignored_risk_keys)

  return { merged, conflicts }
}

// ── 上传循环（OCC + 409 三路合并重试）──────────────────────────────────────

export interface PushDeps {
  /** 上传整包，返回新版本视图；默认走真实 api.uploadVault。 */
  upload?: (baseVersion: number, blobB64: string) => Promise<api.VaultView>
  /** 取服务端最新 vault 并解出 payload（409 后重拉用）。必填（解密需 PIN/VDK，由调用方注入）。 */
  fetchRemote: () => Promise<{ version: number; payload: VaultPayload }>
  maxRetries?: number
}

export interface PushResult {
  version: number
  payload: VaultPayload
  conflicts: MergeConflict[]
}

/**
 * 把本地编辑后的 payload 安全回写云端。
 * @param meta        vault meta（含 key_wrap；编辑不改 meta，改 PIN 另走 P6）
 * @param vdk         字段密钥（reseal payload 用）
 * @param basePayload 拉取/上次同步时的快照（三路合并的 base）
 * @param localPayload 本地编辑后的 payload
 * @param baseVersion 拉取时的版本号（OCC 基线）
 */
export async function pushVault(
  meta: Record<string, unknown>,
  vdk: Uint8Array,
  basePayload: VaultPayload,
  localPayload: VaultPayload,
  baseVersion: number,
  deps: PushDeps,
): Promise<PushResult> {
  const upload = deps.upload ?? api.uploadVault
  const maxRetries = deps.maxRetries ?? 4

  let base = basePayload
  let local = localPayload
  let version = baseVersion
  const allConflicts: MergeConflict[] = []

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const blobB64 = bytesToBase64(packVault(meta, resealPayload(vdk, local)))
    try {
      const view = await upload(version, blobB64)
      return { version: view.version, payload: local, conflicts: allConflicts }
    } catch (e) {
      const conflict = api.asVersionConflict(e)
      if (!conflict || attempt === maxRetries) throw e
      // 拉远端最新，三路合并后以服务端版本为新基线重试。
      const remote = await deps.fetchRemote()
      const { merged, conflicts } = mergePayload(base, local, remote.payload)
      allConflicts.push(...conflicts)
      base = remote.payload
      local = merged
      version = remote.version
    }
  }
  // 理论不可达（循环内要么 return 要么 throw）。
  throw new Error('pushVault: 超过最大重试次数')
}
